import { memo } from 'react';
import { createPortal } from 'react-dom';
import { useAtomValue } from 'jotai/react';
import { contentTransformAtom } from '@user/pages/ViewModelPage/contentTransformAtom';
import { CommentConstants } from '@view-model/models/sticky/ModelComment/constants';

// useViewMenuHandlers でドロップオフセット計算に使用するため export する
export const COMMENT_GHOST_DRAG_HANDLE_HEIGHT = 24;

const DRAG_HANDLE_HEIGHT = COMMENT_GHOST_DRAG_HANDLE_HEIGHT;

// text-3xl (2rem = 32px) の日本語フォント（M PLUS 1p）での line-height: normal の近似値（SVGキャンバス座標単位）
// アバター行は flex items-center で名前テキストの行高が支配するため、
// 明示的な avatar ボックス高（= AVATAR_SIZE + AVATAR_PADDING*2 = 48px）より大きくなる
const TEXT_LINE_HEIGHT = 54;

// アバター行の高さ: 名前テキストの行高が avatar ボックスを上回る
const AVATAR_ROW_HEIGHT = TEXT_LINE_HEIGHT; // 54px
const AVATAR_MARGIN_BOTTOM = 8; // mb-2 = 0.5rem

// テキストエリア: formMinRows × 行高 + p-2 上下パディング + border-2 上下ボーダー
const TEXTAREA_PADDING_VERTICAL = 16; // p-2: 8px × 2辺
const TEXTAREA_BORDER_VERTICAL = 4; // border-2: 2px × 2辺
const TEXTAREA_HEIGHT =
  CommentConstants.UISetting.formMinRows * TEXT_LINE_HEIGHT + TEXTAREA_PADDING_VERTICAL + TEXTAREA_BORDER_VERTICAL;
// = 4 * 54 + 16 + 4 = 236

// フッターボタン: FOOTER_HEIGHT はボタンセクション全体の高さ（上マージン + ボタン矩形）
// ボタンの p-2 上パディングを marginTop として表現し、textarea との間に余白を作る
const BUTTON_MARGIN_TOP = 8; // p-2 top: ボタン上端から背景色が始まる前の余白
const FOOTER_HEIGHT = 60; // BUTTON_MARGIN_TOP + ボタン矩形高さ
const BUTTON_RECT_HEIGHT = FOOTER_HEIGHT - BUTTON_MARGIN_TOP; // 52px
const CLOSE_BUTTON_WIDTH = 118;
const SEND_BUTTON_WIDTH = 94;

// コンテンツ領域のパディング（CreatingModelCommentView の p-4 pb-3 に対応）
const CONTENT_PADDING_TOP = 16; // p-4 = 1rem
const CONTENT_PADDING_BOTTOM = 12; // pb-3 = 0.75rem

// 実フォームの構成要素から計算式で導出（magic number を排除）
// = 16 + 54 + 8 + 236 + 60 + 12 = 386
// DRAG_HANDLE_HEIGHT 分は useViewMenuHandlers 側で別途加算する
export const COMMENT_GHOST_FORM_CONTENT_HEIGHT =
  CONTENT_PADDING_TOP +
  AVATAR_ROW_HEIGHT +
  AVATAR_MARGIN_BOTTOM +
  TEXTAREA_HEIGHT +
  FOOTER_HEIGHT +
  CONTENT_PADDING_BOTTOM;

const FORM_CONTENT_HEIGHT = COMMENT_GHOST_FORM_CONTENT_HEIGHT;

type Props = {
  position: { x: number; y: number } | null;
};

/**
 * コメントのドラッグ中に表示するゴースト。
 * 実際のコメント作成フォーム（CreatingModelCommentView）の外観を模したデザイン。
 * ユーザー名・アバターは省略している。
 */
const CommentDragGhostFn: React.FC<Props> = ({ position }: Props) => {
  const contentTransform = useAtomValue(contentTransformAtom);

  if (!position) return null;

  const scale = contentTransform.k;
  const screenWidth = CommentConstants.UISetting.width * scale;
  const dragHandleHeight = DRAG_HANDLE_HEIGHT * scale;
  const screenHeight = dragHandleHeight + FORM_CONTENT_HEIGHT * scale;

  return createPortal(
    <div
      className="pointer-events-none fixed z-[9999] box-border flex -translate-x-1/2 -translate-y-1/2 flex-col border-2 border-gray-300 bg-white opacity-85"
      style={{
        left: position.x,
        top: position.y,
        width: screenWidth,
        height: screenHeight,
        borderRadius: 16 * scale,
      }}
    >
      {/* ドラッグハンドル（グリップストリップ） */}
      <div className="flex shrink-0 items-center justify-center" style={{ height: dragHandleHeight }}>
        <div className="rounded-full bg-gray-300" style={{ width: 48 * scale, height: 6 * scale }} />
      </div>

      {/* コンテンツ（p-4 top / pb-3 bottom） */}
      <div className="flex-1" style={{ padding: 16 * scale, paddingBottom: 12 * scale }}>
        {/* アバター行の空白（ユーザー名・アバターは省略）
            実フォームでは名前テキストの行高（TEXT_LINE_HEIGHT）がアバターボックス高を上回るため
            AVATAR_ROW_HEIGHT = TEXT_LINE_HEIGHT で合わせる */}
        <div
          style={{
            height: AVATAR_ROW_HEIGHT * scale,
            marginBottom: AVATAR_MARGIN_BOTTOM * scale,
          }}
        />

        {/* テキストエリアのプレースホルダー */}
        <div
          className="box-border border border-gray-400 bg-gray-100"
          style={{ height: TEXTAREA_HEIGHT * scale, borderRadius: 8 * scale }}
        />

        {/* フッター：閉じる・送信ボタン（実フォームの flex justify-end gap-2 に対応）
            marginTop で p-2 上パディング相当の余白を表現する */}
        <div className="flex justify-end" style={{ gap: 8 * scale, marginTop: BUTTON_MARGIN_TOP * scale }}>
          <div
            className="bg-gray-200"
            style={{ height: BUTTON_RECT_HEIGHT * scale, width: CLOSE_BUTTON_WIDTH * scale, borderRadius: 8 * scale }}
          />
          <div
            className="bg-brand opacity-50"
            style={{ height: BUTTON_RECT_HEIGHT * scale, width: SEND_BUTTON_WIDTH * scale, borderRadius: 8 * scale }}
          />
        </div>
      </div>
    </div>,
    document.body
  );
};

export const CommentDragGhost = memo(CommentDragGhostFn);
